import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, setToken, removeToken, getUser, setUser, removeUser, API_URL } from '../lib/utils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      
      if (token) {
        try {
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUserState(userData);
            setUser(userData);
          } else {
            removeToken();
            removeUser();
          }
        } catch (err) {
          console.error('Auth check failed:', err);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    setError(null);
    let res;
    try {
      res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
    } catch (networkError) {
      const errMsg = 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.';
      setError(errMsg);
      throw new Error(errMsg);
    }

    // Read response as text first, then parse as JSON
    let text = '';
    let data = null;
    
    try {
      text = await res.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch (parseError) {
      console.error('Login response parse error:', res.status, text?.substring(0, 200));
      
      // Handle server errors
      if (res.status >= 500) {
        const errMsg = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
        setError(errMsg);
        throw new Error(errMsg);
      }
      const errMsg = 'Beklenmeyen sunucu yanıtı';
      setError(errMsg);
      throw new Error(errMsg);
    }

    if (!res.ok) {
      const errMsg = data?.detail || data?.message || 'Giriş başarısız';
      setError(errMsg);
      throw new Error(errMsg);
    }

    setToken(data.access_token);
    setUser(data.user);
    setUserState(data.user);
    return data;
  };

  const register = async (email, password, name, opts = {}) => {
    setError(null);
    const selectedProduct = opts.selectedProduct === 'life' ? 'life' : 'sales';
    let res;
    try {
      res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, selected_product: selectedProduct })
      });
    } catch (networkError) {
      const errMsg = 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.';
      setError(errMsg);
      throw new Error(errMsg);
    }

    // Read response as text first, then parse as JSON
    let text = '';
    let data = null;
    
    try {
      text = await res.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch (parseError) {
      console.error('Register response parse error:', res.status, text?.substring(0, 200));
      
      if (res.status >= 500) {
        const errMsg = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
        setError(errMsg);
        throw new Error(errMsg);
      }
      const errMsg = 'Beklenmeyen sunucu yanıtı';
      setError(errMsg);
      throw new Error(errMsg);
    }

    if (!res.ok) {
      const errMsg = data?.detail || data?.message || 'Kayıt başarısız';
      setError(errMsg);
      throw new Error(errMsg);
    }

    setToken(data.access_token);
    setUser(data.user);
    setUserState(data.user);
    return data;
  };

  const logout = () => {
    // Hard-clear ALL session state: auth, user cache, draft intakes,
    // hypothesis residue, and any stale Zustand/context that may be
    // hanging on to in-memory references. Then hard-redirect to homepage
    // so React state is fully torn down on the next load.
    try {
      removeToken();
      removeUser();
      // Sweep any other lifeos_* keys (intake drafts, locale prefs, dashboard cache)
      Object.keys(localStorage)
        .filter((k) => k.startsWith('lifeos_') || k === 'mife_intake_draft' || k.startsWith('cd_'))
        .forEach((k) => localStorage.removeItem(k));
      // sessionStorage too — onboarding step / hypotheses live here in some flows
      sessionStorage.clear();
    } catch (e) {
      // Never let a storage hiccup block logout.
      console.warn('logout: storage clear partial failure', e);
    }
    setUserState(null);
    // Hard reload (not navigate) so React tree + any context state is fully reset.
    window.location.assign('/');
  };

  const refreshUser = async () => {
    const token = getToken();
    if (token) {
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUserState(userData);
          setUser(userData);
        }
      } catch (err) {
        console.error('User refresh failed:', err);
      }
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
